import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuthContext } from "../hooks/useAuthContext";
import { useUpgradeModal } from "../Components/UpgradeModal";
import { effectivePlanLabel } from "../utils/tier";
import {
    fetchPlanState,
    fetchUsage,
    fetchAddonCatalog,
    fetchActiveAddons,
    fetchByok,
    saveByok,
    removeByok,
    cancelPlan,
    reactivatePlan,
    purchaseAddon,
} from "../lib/billingApi";

const RESOURCE_LABELS = {
    responses: "Form responses",
    ai_generations: "AI generations",
};

const STATUS_STYLES = {
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    authenticated: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    created: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    cancelled: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    canceled: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    halted: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    none: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
};

function addonPrice(addon, currency) {
    if (currency === "INR") return `₹${(addon.pricing.inrWithGst / 100).toLocaleString("en-IN")}`;
    return `$${(addon.pricing.usd / 100).toFixed(2)}`;
}

function fmtLimit(v) {
    if (v === -1) return "Unlimited";
    return Number(v).toLocaleString();
}

function UsageBar({ resource, m }) {
    const label = RESOURCE_LABELS[resource] || resource;
    const pct = m.unlimited ? 0 : Math.round((m.pct || 0) * 100);
    const barColor = m.exceeded
        ? "bg-rose-500"
        : m.warn
        ? "bg-amber-500"
        : "bg-indigo-500";
    return (
        <div className="py-3">
            <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-800 dark:text-gray-200">{label}</span>
                <span className="text-gray-500 dark:text-gray-400 font-display">
                    {m.used.toLocaleString()}
                    {m.unlimited ? "" : ` / ${fmtLimit(m.limit)}`}
                    {m.unlimited && <span className="ml-1 text-emerald-600 dark:text-emerald-400">Unlimited</span>}
                </span>
            </div>
            {!m.unlimited && (
                <div className="mt-2 h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700/60 overflow-hidden">
                    <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
            )}
            {m.exceeded && (
                <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
                    Limit reached — add a capacity boost or upgrade to keep going.
                </p>
            )}
            {!m.exceeded && m.warn && (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                    You've used {pct}% of your monthly {label.toLowerCase()}.
                </p>
            )}
        </div>
    );
}

function BillingDashboard() {
    const { user } = useAuthContext();
    const { openUpgradeModal } = useUpgradeModal();
    const navigate = useNavigate();
    const location = useLocation();

    const [planState, setPlanState] = useState(null);
    const [usage, setUsage] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [activeAddons, setActiveAddons] = useState([]);
    const [byok, setByok] = useState({ active: false, last4: "" });
    const [keyInput, setKeyInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [currency, setCurrency] = useState(() => localStorage.getItem("cfai_currency") || "INR");

    const load = async () => {
        try {
            const [ps, us, cat, active, bk] = await Promise.all([
                fetchPlanState(),
                fetchUsage().catch(() => null),
                fetchAddonCatalog().catch(() => ({ addons: [] })),
                fetchActiveAddons().catch(() => ({ addons: [] })),
                fetchByok().catch(() => ({ active: false, last4: "" })),
            ]);
            setPlanState(ps);
            setUsage(us);
            setCatalog(cat.addons || []);
            setActiveAddons(active.addons || []);
            setByok(bk);
            if (!localStorage.getItem("cfai_currency") && ps?.currency) setCurrency(ps.currency);
        } catch (e) {
            toast(e.message || "Failed to load billing");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deep-link from the BYOK nudge banner (/settings/billing?scroll=byok) → scroll to the card.
    useEffect(() => {
        if (loading) return;
        if (new URLSearchParams(location.search).get("scroll") === "byok") {
            document.getElementById("byok")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [loading, location.search]);

    const setCur = (c) => { setCurrency(c); localStorage.setItem("cfai_currency", c); };

    const status = planState?.subscription_status || "none";
    const isActivePaid = planState?.has_paid_subscription && ["active", "authenticated"].includes(status);
    const scheduledCancel = !!planState?.cancel_at_period_end;
    const isCancellable = isActivePaid && !scheduledCancel;
    const isReactivatable = planState?.has_paid_subscription && ["cancelled", "canceled", "halted"].includes(status);
    const periodEndLabel = planState?.current_period_end
        ? new Date(planState.current_period_end).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
        : null;

    const warnings = usage?.warnings || [];

    // BYOK unlocks on LTD Tier 3+ (3 stacked codes). For an LTD buyer below that, the upgrade
    // path is stacking more codes — not a SaaS Pro subscription — so we steer them to /redeem.
    const codesToTier3 = Math.max(0, 3 - (planState?.ltd_code_count || 0));

    const handleCancel = async () => {
        if (!window.confirm("Cancel your plan at the end of the current billing period?")) return;
        setBusy(true);
        try { await cancelPlan(); toast("Plan will cancel at period end."); await load(); }
        catch (e) { toast(e.message); }
        finally { setBusy(false); }
    };

    const handleReactivate = async () => {
        setBusy(true);
        try { await reactivatePlan(); toast("Plan reactivated."); await load(); }
        catch (e) { toast(e.message); }
        finally { setBusy(false); }
    };

    const buyAddon = (addon) => {
        setBusy(true);
        purchaseAddon(
            { sku: addon.sku, currency, name: addon.name },
            {
                onSuccess: async () => { toast(`${addon.name} added.`); await load(); setBusy(false); },
                onDismiss: () => setBusy(false),
                onError: (e) => { toast(e.message || "Add-on failed"); setBusy(false); },
            }
        ).catch((e) => { toast(e.message || "Could not start checkout"); setBusy(false); });
    };

    const openByokUpgrade = () =>
        openUpgradeModal({ trigger: "feature_gate", requestedFeature: "byok_available", recommendedPlan: "pro", dismissable: true });

    const handleSaveKey = async () => {
        setBusy(true);
        try {
            const r = await saveByok(keyInput.trim());
            setByok({ active: true, last4: r.last4 });
            setKeyInput("");
            toast("Your OpenAI key is now active.");
            await load();
        } catch (e) {
            if (e.requiresUpgrade) {
                if (planState?.has_ltd) navigate("/redeem");
                else openByokUpgrade();
            }
            else { toast(e.message); }
        }
        finally { setBusy(false); }
    };

    const handleRemoveKey = async () => {
        setBusy(true);
        try { await removeByok(); setByok({ active: false, last4: "" }); toast("Reverted to the platform key."); await load(); }
        catch (e) { toast(e.message); }
        finally { setBusy(false); }
    };

    const meterEntries = useMemo(
        () => (usage?.meters ? Object.entries(usage.meters) : []),
        [usage]
    );

    if (!user) { navigate("/"); return null; }

    if (loading) {
        return <div className="min-h-screen max-w-4xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Loading billing…</div>;
    }

    return (
        <div className="min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-8 font-Rajdhani">
            <Helmet><title>Billing — Content Flash AI</title></Helmet>
            <ToastContainer position="top-right" autoClose={2500} theme="light" />

            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Your plan, usage this period, and capacity boosters.</p>
                </div>
                <div className="inline-flex text-xs rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
                    <button onClick={() => setCur("USD")} className={`px-3 py-1.5 rounded-md ${currency === "USD" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>USD</button>
                    <button onClick={() => setCur("INR")} className={`px-3 py-1.5 rounded-md ${currency === "INR" ? "bg-white dark:bg-gray-700 shadow-sm" : "text-gray-500"}`}>INR</button>
                </div>
            </div>

            {/* Usage warnings banner */}
            {warnings.length > 0 && (
                <div className="mb-5 rounded-xl border border-amber-200 dark:border-amber-700/50 bg-amber-50/70 dark:bg-amber-900/15 px-4 py-3">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                        {warnings.some((w) => w.exceeded)
                            ? "You've hit a usage limit this period. Add a capacity boost below or upgrade your plan."
                            : "You're approaching a usage limit this period. Consider a capacity boost or an upgrade."}
                    </p>
                </div>
            )}

            {/* Current plan */}
            <section className="rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-[#23263a] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{effectivePlanLabel(planState)} plan</h2>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${scheduledCancel ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" : (STATUS_STYLES[status] || STATUS_STYLES.none)}`}>
                                {scheduledCancel ? "Cancelling" : status}
                            </span>
                        </div>
                        {scheduledCancel && (
                            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                                Scheduled to cancel{periodEndLabel ? ` on ${periodEndLabel}` : " at the end of your billing period"}. You keep {effectivePlanLabel(planState)} features until then; after that your account returns to Free.
                            </p>
                        )}
                        {planState.has_ltd && (
                            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
                                AppSumo Lifetime Deal
                                {planState.ltd_code_count ? ` · ${planState.ltd_code_count} code${planState.ltd_code_count > 1 ? "s" : ""} stacked` : ""}
                            </p>
                        )}
                        {planState.byok_active && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Using your own OpenAI key (BYOK)</p>
                        )}
                        {planState.priority_support && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                ⭐ Priority support active
                                {planState.priority_support_until ? ` until ${new Date(planState.priority_support_until).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {planState.upsell_eligible && (
                            <Link to="/settings/plans" className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold no-underline whitespace-nowrap">
                                {planState.has_ltd && !planState.has_paid_subscription ? "Add a plan →" : "Upgrade →"}
                            </Link>
                        )}
                        <Link to="/settings/plans" className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#2c2f44] no-underline text-gray-800 dark:text-gray-200 whitespace-nowrap">
                            Compare plans
                        </Link>
                        {isCancellable && (
                            <button disabled={busy} onClick={handleCancel} className="px-4 py-2 rounded-xl border border-rose-300 dark:border-rose-700/60 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/15 disabled:opacity-50">
                                Cancel plan
                            </button>
                        )}
                        {isReactivatable && (
                            <button disabled={busy} onClick={handleReactivate} className="px-4 py-2 rounded-xl border border-emerald-300 dark:border-emerald-700/60 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/15 disabled:opacity-50">
                                Reactivate
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Usage this period */}
            <section className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-[#23263a] p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Usage this period</h2>
                {meterEntries.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Usage data is unavailable right now.</p>
                ) : (
                    <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700/50">
                        {meterEntries.map(([resource, m]) => (
                            <UsageBar key={resource} resource={resource} m={m} />
                        ))}
                    </div>
                )}
            </section>

            {/* Capacity boosters */}
            <section className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-[#23263a] p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Boosters & add-ons</h2>
                    <span className="text-sm text-gray-600 dark:text-gray-400">One-time purchase</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Need more without upgrading? Top up a resource for the current period, or permanently unlock a feature.
                </p>

                {activeAddons.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {activeAddons.map((a) => (
                            <span key={a._id} className="text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300">
                                Active: {a.addonSku}{a.qty > 1 ? ` ×${a.qty}` : ""}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {catalog.map((addon) => {
                        // A permanent feature unlock (e.g. custom domain) the workspace already
                        // has — via its plan tier or a prior purchase — shouldn't be re-sold.
                        const owned = addon.scope === "permanent" && addon.featureKey && planState?.[addon.featureKey] === true;
                        // Workflow boost needs Agent Actions (agent_workflows_per_form === 0 ⇒ none).
                        const needsAgentActions = addon.requiresFeature === "agent_actions" && (planState?.agent_workflows_per_form ?? 0) === 0;
                        // Paid-plan-only add-ons (e.g. custom domain) aren't sold to Free users.
                        const needsPaid = addon.requiresPaidPlan && planState?.effective_plan === "free" && !planState?.has_ltd;
                        const locked = needsAgentActions || needsPaid;
                        return (
                            <div key={addon.sku} className="flex flex-col justify-between rounded-xl border border-gray-200 dark:border-gray-700/60 p-4">
                                <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                        {addon.name}
                                        {addon.scope === "permanent" && <span className="ml-2 text-[10px] uppercase tracking-wide text-indigo-500 dark:text-indigo-400 font-semibold">One-time</span>}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{addon.description}</p>
                                    {needsAgentActions && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Requires a plan with Agent Actions (Pro or higher).</p>}
                                    {needsPaid && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Available on paid plans — upgrade to unlock.</p>}
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{addonPrice(addon, currency)}</span>
                                    {owned ? (
                                        <span className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs font-semibold">Owned</span>
                                    ) : locked ? (
                                        <Link to="/settings/plans" className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-xs font-semibold no-underline">
                                            Upgrade
                                        </Link>
                                    ) : (
                                        <button disabled={busy} onClick={() => buyAddon(addon)} className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50">
                                            Add
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* BYOK — bring your own OpenAI key */}
            <section id="byok" className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-[#23263a] p-6 scroll-mt-24">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Bring your own OpenAI key</h2>
                    {byok.active ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Active</span>
                    ) : planState?.byok_available ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">Save cost on unlimited AI</span>
                    ) : null}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Use your own key for AI form generation and response analysis. While active, those AI
                    calls bill to your OpenAI account and don't count against your monthly generation limit.
                </p>

                {byok.active ? (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Key ending in <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/60">••••{byok.last4}</code>
                        </span>
                        <button disabled={busy} onClick={handleRemoveKey} className="px-3 py-1.5 rounded-lg border border-rose-300 dark:border-rose-700/60 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/15 disabled:opacity-50">
                            Remove key
                        </button>
                    </div>
                ) : planState?.byok_available === false ? (
                    planState?.has_ltd ? (
                        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40 px-4 py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                🔒 BYOK unlocks at Lifetime Tier 3.{" "}
                                {codesToTier3 > 0
                                    ? `Stack ${codesToTier3} more code${codesToTier3 === 1 ? "" : "s"} to reach it.`
                                    : "Stack codes to reach it."}
                            </span>
                            <Link to="/redeem" className="ml-auto px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold no-underline">
                                Stack codes →
                            </Link>
                        </div>
                    ) : (
                        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40 px-4 py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                🔒 BYOK is available on Pro plans and above.
                            </span>
                            <button onClick={openByokUpgrade} className="ml-auto px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
                                Upgrade to unlock
                            </button>
                        </div>
                    )
                ) : (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <input
                            type="password"
                            value={keyInput}
                            onChange={(e) => setKeyInput(e.target.value)}
                            placeholder="sk-…"
                            autoComplete="off"
                            className="flex-1 min-w-[220px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-slate-200 text-sm"
                        />
                        <button disabled={busy || !keyInput.trim()} onClick={handleSaveKey} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                            Save key
                        </button>
                    </div>
                )}
                <p className="text-[11px] text-gray-400 mt-2">Your key is validated with OpenAI and stored encrypted. We never display it back.</p>
            </section>
        </div>
    );
}

export default BillingDashboard;
